import React, { useMemo, useState } from "react";
import { fmtLimit, effectiveLimit, effectiveTypeLimit, shopTypes, shopResources } from "../../../Booking/bookingPlans";

/**
 * Owner-editable plan limits. Shows which shops would move over/under
 * the proposed caps before saving.
 */
export default function PlansTab({ plans, shops, resourceTypes, resources, onSave, saving }) {
    const [draft, setDraft] = useState(() => JSON.parse(JSON.stringify(plans || {})));
    const [formError, setFormError] = useState("");

    const set = (planId, key, raw) => {
        setDraft((prev) => ({
            ...prev,
            [planId]: { ...prev[planId], [key]: raw === "" ? null : Math.max(0, parseInt(raw, 10) || 0) },
        }));
    };

    const setMeta = (planId, key, value) => {
        setDraft((prev) => ({
            ...prev,
            [planId]: { ...prev[planId], [key]: value },
        }));
    };

    // Shops whose *plan defaults* (no override) would leave them over cap.
    const impacted = useMemo(() => {
        const out = [];
        for (const s of shops) {
            const resN = shopResources(resources, s.id).length;
            const typeN = shopTypes(resourceTypes, s.id).length;
            const resCap = draft[s.plan]?.resourceLimit ?? null;
            const typeCap = draft[s.plan]?.typeLimit ?? null;
            const noResOverride = s.resourceLimitOverride === null || s.resourceLimitOverride === undefined;
            const noTypeOverride = s.typeLimitOverride === null || s.typeLimitOverride === undefined;
            const overRes = noResOverride && resCap !== null && resN > resCap;
            const overTypes = noTypeOverride && typeCap !== null && typeN > typeCap;
            if (overRes || overTypes) {
                out.push({
                    shop: s,
                    overRes: overRes ? { have: resN, cap: resCap } : null,
                    overTypes: overTypes ? { have: typeN, cap: typeCap } : null,
                });
            }
        }
        return out;
    }, [shops, resources, resourceTypes, draft]);

    const dirty = JSON.stringify(draft) !== JSON.stringify(plans);

    const handleSave = () => {
        for (const id of ["free", "pro"]) {
            const p = draft[id];
            if (!p?.label?.trim()) {
                setFormError("Each plan needs a label.");
                return;
            }
            if (p.resourceLimit !== null && (!Number.isInteger(p.resourceLimit) || p.resourceLimit < 1)) {
                setFormError(`${p.label}: resource limit must be at least 1.`);
                return;
            }
        }
        setFormError("");
        onSave(draft);
    };

    return (
        <div className="space-y-4">
            {formError && <div className="alert alert-danger py-2 mb-0">{formError}</div>}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {["free", "pro"].map((id) => {
                    const p = draft[id] || {};
                    return (
                        <div key={id} className={id === "pro" ? "hk-card hk-card-dark hk-ticket" : "hk-card"}>
                            <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
                                <input
                                    className={`hk-input hk-display font-bold text-lg ${id === "pro" ? "" : ""}`}
                                    style={id === "pro" ? { background: "#1e293b", borderColor: "#334155", color: "#fff", maxWidth: 200 } : { maxWidth: 200 }}
                                    value={p.label || ""}
                                    onChange={(e) => setMeta(id, "label", e.target.value)}
                                />
                                <div className="flex items-center gap-2">
                                    <span className="text-xs font-bold opacity-70">{p.currency || ""}</span>
                                    <input
                                        className="hk-input hk-mono"
                                        style={id === "pro" ? { background: "#1e293b", borderColor: "#334155", color: "#fff", maxWidth: 110 } : { maxWidth: 110 }}
                                        type="number" min="0"
                                        value={p.price ?? 0}
                                        onChange={(e) => setMeta(id, "price", Math.max(0, parseFloat(e.target.value) || 0))}
                                        title="Monthly price"
                                    />
                                    <span className="text-xs opacity-70">/mo</span>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="hk-label" style={id === "pro" ? { color: "#94a3b8" } : undefined}>
                                        Resources (per shop)
                                    </label>
                                    <input
                                        className="hk-input hk-mono"
                                        style={id === "pro" ? { background: "#1e293b", borderColor: "#334155", color: "#fff" } : undefined}
                                        type="number" min="1"
                                        value={p.resourceLimit ?? ""}
                                        placeholder="∞"
                                        onChange={(e) => set(id, "resourceLimit", e.target.value)}
                                    />
                                </div>
                                <div>
                                    <label className="hk-label" style={id === "pro" ? { color: "#94a3b8" } : undefined}>
                                        Types (blank = ∞)
                                    </label>
                                    <input
                                        className="hk-input hk-mono"
                                        style={id === "pro" ? { background: "#1e293b", borderColor: "#334155", color: "#fff" } : undefined}
                                        type="number" min="0"
                                        value={p.typeLimit ?? ""}
                                        placeholder="∞"
                                        onChange={(e) => set(id, "typeLimit", e.target.value)}
                                    />
                                </div>
                            </div>
                            <input
                                className="hk-input mt-2"
                                style={id === "pro" ? { background: "#1e293b", borderColor: "#334155", color: "#fff" } : undefined}
                                value={p.description || ""}
                                onChange={(e) => setMeta(id, "description", e.target.value)}
                                placeholder="Plan description"
                            />
                            <p className={`text-xs mt-3 mb-0 ${id === "pro" ? "text-[#94a3b8]" : "text-[#64748b]"}`}>
                                {fmtLimit(p.resourceLimit)} bookable resources · {fmtLimit(p.typeLimit)} resource types per shop.
                                Per-shop overrides (in Configure) always win over these defaults.
                            </p>
                        </div>
                    );
                })}
            </div>

            {impacted.length > 0 && (
                <div className="hk-card" style={{ borderColor: "#fde68a", background: "#fffbeb" }}>
                    <h5 className="hk-card-title" style={{ color: "#b45309" }}>⚠ Shops that would go over cap</h5>
                    <div className="space-y-2">
                        {impacted.map(({ shop, overRes, overTypes }) => (
                            <div key={shop.id} className="text-sm">
                                <strong>{shop.business}</strong>
                                <span className="text-[#64748b]"> ({shop.plan}) — </span>
                                {overRes && <span className="hk-mono text-xs">{overRes.have}/{overRes.cap} resources </span>}
                                {overTypes && <span className="hk-mono text-xs">{overTypes.have}/{overTypes.cap} types</span>}
                                <span className="text-xs text-[#64748b]"> · extras freeze, nothing is deleted</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="flex justify-end">
                <button className="hk-btn hk-btn-primary" disabled={!dirty || !!saving} onClick={handleSave}>
                    {saving ? "Saving…" : "Save plan limits"}
                </button>
            </div>
        </div>
    );
}

export { effectiveLimit, effectiveTypeLimit };

